import { QBankStructureLevelCategory, type QBankStructureLevel } from '@exam-management/graphql';
import { Checkbox, Flex, type FlexProps, styled } from '@shared/components';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { getQBankStructureLevelLabelByCategory } from '../../utils';

import RequireAdornment from './require-adornment';

const CheckboxWrapper = styled(Flex)(({ theme }) => ({
  borderRadius: theme.shape.rounded(2),
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  padding: theme.spacing(3, 4),
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const REQUIRED_LEVELS = [
  QBankStructureLevelCategory.Course,
  QBankStructureLevelCategory.Chapter,
  QBankStructureLevelCategory.SubTopic,
];

function getQBankStructureLevelLabel(category: QBankStructureLevelCategory, level: number) {
  return `Level ${level}: ${getQBankStructureLevelLabelByCategory(category, { withEng: true })}`;
}

export interface QBSLevelCheckboxGroupProps extends FlexProps {
  qBankStructureLevels: Omit<QBankStructureLevel, 'qBankStructureId'>[];
  disabled?: boolean;
}

export default function QBSLevelCheckboxGroup(props: Readonly<QBSLevelCheckboxGroupProps>) {
  const { disabled: isDisabled = false, qBankStructureLevels, ...restProps } = props;

  const sortedQBSLevels = useMemo(
    () => [...qBankStructureLevels].sort((a, b) => a.level - b.level),
    [qBankStructureLevels],
  );

  return (
    <Flex
      {...restProps}
      sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(3), ...restProps?.sx }}
    >
      {sortedQBSLevels.map((qBankStructureLevel) => {
        const { category, level, enabled: isEnabled } = qBankStructureLevel;

        const qBankStructureLevelLabel = getQBankStructureLevelLabel(category, level);

        const isRequiredLevel = REQUIRED_LEVELS.includes(category);
        const shouldDisabled = isDisabled || isRequiredLevel;

        return (
          <Controller
            key={`setup-qbs-level-${qBankStructureLevel.id}`}
            name={qBankStructureLevel.category}
            disabled={shouldDisabled}
            defaultValue={isEnabled}
            render={({ field }) => {
              return (
                <CheckboxWrapper
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (field.disabled) return;

                    field.onChange(!field.value);
                  }}
                  sx={(theme) => ({
                    cursor: field.disabled ? 'not-allowed' : 'pointer',
                    bgcolor: field.disabled
                      ? theme.palette.grey[100]
                      : field.value
                        ? theme.palette.success.lighter
                        : theme.palette.background.paper,
                    borderColor:
                      field.value && !field.disabled ? theme.palette.success.main : undefined,
                  })}
                >
                  <Checkbox
                    {...field}
                    label={qBankStructureLevelLabel}
                    color='success'
                    sx={(theme) => ({
                      p: theme.spacing(0),
                      color: theme.palette.grey[400],
                      '&.Mui-disabled': {
                        color: theme.palette.grey[300],
                      },
                    })}
                    slotProps={{
                      formControlLabel: {
                        sx: (theme) => ({
                          gap: theme.spacing(4),
                          '& .MuiFormControlLabel-label.Mui-disabled': {
                            color: theme.palette.text.primary,
                          },
                          flex: 1,
                          '&.Mui-disabled': {
                            cursor: field.disabled ? 'not-allowed' : 'pointer',
                          },
                        }),
                      },
                      typography: {
                        variant: 'body1Semibold',
                      },
                    }}
                  />
                  {isRequiredLevel ? <RequireAdornment /> : null}
                </CheckboxWrapper>
              );
            }}
          />
        );
      })}
    </Flex>
  );
}
